import React, { useEffect, useState } from "react";
import {
  Box,
  Grid,
  Typography,
  IconButton,
  Card,
  CardContent,
  Container,
  Fade,
  Zoom,
  Snackbar,
  Alert,
  styled,
  Backdrop,
  CircularProgress,
  Chip,
  Button,
} from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import {
  DeleteForever,
  Work,
  Email,
  Phone,
  LocationOn,
  School,
  Description,
  Person,
  Download,
} from "@mui/icons-material";

import { deleteApplication, fetchApplication } from "../../actions/career";

const StyledContainer = styled(Box)(({ theme }) => ({
  minHeight: "100vh",
  background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
  paddingTop: "2rem",
  paddingBottom: "2rem",
  width: "100%",
}));

const ApplicationCard = styled(Card)(({ theme }) => ({
  background: "rgba(255, 255, 255, 0.95)",
  backdropFilter: "blur(10px)",
  borderRadius: "16px",
  border: "1px solid rgba(255, 255, 255, 0.2)",
  boxShadow: "0 4px 20px rgba(0, 0, 0, 0.1)",
  transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
  position: "relative",
  overflow: "hidden",
  width: "100%",
  maxWidth: "600px",
  margin: "0 auto",
  "&:hover": {
    transform: "translateY(-4px)",
    boxShadow: "0 8px 30px rgba(0, 0, 0, 0.15)",
  },
  "&::before": {
    content: '""',
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    height: "4px",
    background: "linear-gradient(90deg, #667eea 0%, #764ba2 100%)",
  },
}));

const DeleteButton = styled(IconButton)(({ theme }) => ({
  position: "absolute",
  top: "16px",
  right: "16px",
  background: "rgba(244, 67, 54, 0.1)",
  color: "#f44336",
  zIndex: 10,
  "&:hover": {
    background: "rgba(244, 67, 54, 0.2)",
    transform: "scale(1.1)",
  },
  transition: "all 0.2s ease",
}));

const SectionTitle = styled(Typography)(({ theme }) => ({
  color: "white",
  fontWeight: "700",
  marginBottom: "2rem",
  textAlign: "center",
  textShadow: "0 2px 4px rgba(0, 0, 0, 0.3)",
}));

const InfoRow = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  marginBottom: "12px",
  gap: "12px",
}));

const InfoLabel = styled(Typography)(({ theme }) => ({
  fontWeight: "600",
  color: "#374151",
  fontSize: "0.9rem",
  minWidth: "140px",
  display: "flex",
  alignItems: "center",
  gap: "8px",
}));

const InfoValue = styled(Typography)(({ theme }) => ({
  color: "#1f2937",
  fontSize: "0.9rem",
  flex: 1,
  wordBreak: "break-word",
}));

const StatsChip = styled(Chip)(({ theme }) => ({
  background: "linear-gradient(135deg, #10b981 0%, #059669 100%)",
  color: "white",
  fontWeight: "600",
  fontSize: "0.75rem",
  height: "24px",
  "& .MuiChip-label": {
    padding: "0 8px",
  },
}));

const ResumeButton = styled(Button)(({ theme }) => ({
  background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
  color: "white",
  borderRadius: "8px",
  padding: "6px 12px",
  textTransform: "none",
  fontSize: "0.8rem",
  fontWeight: "600",
  minWidth: "auto",
  "&:hover": {
    background: "linear-gradient(135deg, #5a67d8 0%, #6b46c1 100%)",
    transform: "translateY(-1px)",
  },
  transition: "all 0.2s ease",
}));

const CareerApplications = () => {
  const dispatch = useDispatch();
  const { careerApplications: applications } = useSelector(
    (state) => state.adminSlice,
  );
  const [loading, setLoading] = useState(false);
  const [toast, setToast] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  const deleteOneApplication = async (id) => {
    setLoading(true);
    try {
      dispatch(deleteApplication({ id }));
      setToast({
        open: true,
        message: "Application deleted successfully!",
        severity: "success",
      });
    } catch (error) {
      setToast({
        open: true,
        message: "Failed to delete application",
        severity: "error",
      });
    }
    setLoading(false);
  };

  const handleResumeClick = (url, name) => {
    if (url) {
      window.open(url, "_blank");
      setToast({
        open: true,
        message: `Opening ${name}'s resume...`,
        severity: "info",
      });
    } else {
      setToast({
        open: true,
        message: "Resume URL not available",
        severity: "warning",
      });
    }
  };

  useEffect(() => {
    dispatch(fetchApplication());
  }, []);

  if (!applications || applications.length === 0) {
    return (
      <StyledContainer>
        <Container maxWidth="lg">
          <SectionTitle variant="h4">
            <Work sx={{ mr: 1, verticalAlign: "middle" }} />
            Career Applications
          </SectionTitle>
          <Box sx={{ textAlign: "center", mt: 4 }}>
            <Work
              sx={{
                fontSize: "4rem",
                color: "rgba(255, 255, 255, 0.7)",
                mb: 2,
              }}
            />
            <Typography variant="h6" sx={{ color: "white", mb: 1 }}>
              No Applications Found
            </Typography>
            <Typography
              variant="body2"
              sx={{ color: "rgba(255, 255, 255, 0.8)" }}
            >
              There are currently no career applications to display.
            </Typography>
          </Box>
        </Container>
      </StyledContainer>
    );
  }

  return (
    <StyledContainer>
      <Container maxWidth="lg">
        <Backdrop
          open={loading}
          sx={{
            zIndex: 1010,
            backdropFilter: "blur(4px)",
          }}
        >
          <CircularProgress size={60} thickness={4} sx={{ color: "#667eea" }} />
        </Backdrop>

        <SectionTitle variant="h4">
          <Work sx={{ mr: 1, verticalAlign: "middle" }} />
          Career Applications
        </SectionTitle>

        <Box sx={{ display: "flex", justifyContent: "center", mb: 2 }}>
          <StatsChip
            label={`${applications.length} Application${applications.length !== 1 ? "s" : ""} Found`}
            icon={<Work sx={{ fontSize: "1rem !important" }} />}
          />
        </Box>

        <Grid container spacing={3}>
          {applications.map((applicant, index) => (
            <Grid item xs={12} lg={6} key={applicant._id}>
              <Zoom in={true} style={{ transitionDelay: `${index * 100}ms` }}>
                <ApplicationCard>
                  <DeleteButton
                    onClick={() => deleteOneApplication(applicant._id)}
                    size="small"
                  >
                    <DeleteForever />
                  </DeleteButton>

                  <CardContent sx={{ p: 3, pr: 6 }}>
                    {/* Header with Name */}
                    <Box sx={{ mb: 3 }}>
                      <Typography
                        variant="h6"
                        sx={{
                          fontWeight: "700",
                          color: "#1f2937",
                          fontSize: "1.2rem",
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                          mb: 1,
                        }}
                      >
                        <Person sx={{ color: "#667eea" }} />
                        {applicant.name}
                      </Typography>
                    </Box>

                    {/* Contact Information */}
                    <Box sx={{ mb: 2 }}>
                      <InfoRow>
                        <InfoLabel>
                          <Email sx={{ fontSize: "1rem", color: "#667eea" }} />
                          Email:
                        </InfoLabel>
                        <InfoValue>{applicant.email}</InfoValue>
                      </InfoRow>

                      <InfoRow>
                        <InfoLabel>
                          <Phone sx={{ fontSize: "1rem", color: "#667eea" }} />
                          Mobile:
                        </InfoLabel>
                        <InfoValue>{applicant.phone_no}</InfoValue>
                      </InfoRow>

                      <InfoRow>
                        <InfoLabel>
                          <LocationOn
                            sx={{ fontSize: "1rem", color: "#667eea" }}
                          />
                          Location:
                        </InfoLabel>
                        <InfoValue>{applicant.location}</InfoValue>
                      </InfoRow>
                    </Box>

                    {/* Professional Details */}
                    <Box
                      sx={{
                        background: "rgba(103, 126, 234, 0.05)",
                        borderRadius: "8px",
                        padding: "12px",
                        border: "1px solid rgba(103, 126, 234, 0.1)",
                      }}
                    >
                      <InfoRow sx={{ mb: 1 }}>
                        <InfoLabel>
                          <School sx={{ fontSize: "1rem", color: "#667eea" }} />
                          Qualification:
                        </InfoLabel>
                        <InfoValue sx={{ fontWeight: "600", color: "#667eea" }}>
                          {applicant.highest_qualification}
                        </InfoValue>
                      </InfoRow>

                      <InfoRow sx={{ mb: 0 }}>
                        <InfoLabel>
                          <Description
                            sx={{ fontSize: "1rem", color: "#667eea" }}
                          />
                          Resume:
                        </InfoLabel>
                        <ResumeButton
                          onClick={() =>
                            handleResumeClick(
                              applicant.resume_url,
                              applicant.name,
                            )
                          }
                          startIcon={<Download sx={{ fontSize: "1rem" }} />}
                          size="small"
                        >
                          View Resume
                        </ResumeButton>
                      </InfoRow>
                    </Box>
                  </CardContent>
                </ApplicationCard>
              </Zoom>
            </Grid>
          ))}
        </Grid>

        {/* Toast Notification */}
        <Snackbar
          open={toast.open}
          autoHideDuration={4000}
          onClose={() => setToast({ ...toast, open: false })}
          anchorOrigin={{ vertical: "top", horizontal: "right" }}
        >
          <Alert
            onClose={() => setToast({ ...toast, open: false })}
            severity={toast.severity}
            variant="filled"
            sx={{
              borderRadius: "12px",
              boxShadow: "0 4px 20px rgba(0, 0, 0, 0.15)",
            }}
          >
            {toast.message}
          </Alert>
        </Snackbar>
      </Container>
    </StyledContainer>
  );
};

export default CareerApplications;
